import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Menu,
  ChevronDown,
  Building,
  FolderOpen,
  Home,
  ExternalLink,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useQuery } from "@tanstack/react-query";

import { getAutodeskConnectionStatus } from "@/lib/api";
import { apiRequest } from "@/lib/queryClient";
import type { AutodeskConnectionStatus } from "@/lib/types";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAppStore } from "@/lib/store";
import type { Project } from "@/lib/store";
import { useAuth } from "@/hooks/useAuth";
import CONFIG from "@/lib/config";

interface HeaderProps {
  toggleSidebar: () => void;
  sidebarOpen?: boolean;
}

function Header({ toggleSidebar, sidebarOpen = true }: HeaderProps) {
  const { selectedProject, setSelectedProject } = useAppStore();
  const { logout } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [location, navigate] = useLocation();

  // Check if we're on Vercel deployment
  const isVercelDeployment = typeof window !== 'undefined' && window.location.hostname.includes('vercel.app');

  // Check if Autodesk connection is configured (disabled for Vercel)
  const { data: connectionStatus } = useQuery<AutodeskConnectionStatus>({
    queryKey: ["/api/autodesk-connection-status"],
    queryFn: getAutodeskConnectionStatus,
    staleTime: 0, // Always refetch when needed
    gcTime: 0, // Don't cache results
    enabled: false, // Disabled - OAuth connection always works
  });

  // Get user's accessible projects based on OAuth permissions (same as MyHome page)
  const {
    data: autodeskProjectsData,
    isLoading: isLoadingProjects,
    isError,
    error,
  } = useQuery({
    queryKey: ["user-projects-oauth-v2"], // Updated to use new API
    queryFn: async () => {
      console.log("Fetching user projects based on OAuth permissions...");
      const data = await apiRequest("/api/autodesk/user-projects-with-subscription");
      console.log("Received user projects data:", data);
      return data;
    },
    enabled: true, // Always enabled - OAuth working everywhere
    retry: (failureCount, error) => {
      // Don't retry on 401 errors
      if (error?.message?.includes('401')) {
        return false;
      }
      return failureCount < 1;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - reduce API calls
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false, // Disable refetch on window focus
    refetchInterval: false, // Disable automatic refetching
  });

  // Memoize the projects array to prevent re-creation on every render
  const projects: Project[] = useMemo(() => {
    // REMOVED: No longer use demo projects for Vercel - use real Autodesk data everywhere
    // OAuth is working, so always use real data

    // Debug logging - only in verbose development mode
    if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_VERBOSE === 'true') {
      console.log("Mapping user projects...", {
        isError,
        hasResults: !!autodeskProjectsData?.results,
        resultsLength: autodeskProjectsData?.results?.length,
        total: autodeskProjectsData?.total,
        rawResults: autodeskProjectsData?.results
      });
    }
    
    if (isError || !autodeskProjectsData?.results) {
      // Debug logging - only in verbose development mode
      if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_VERBOSE === 'true') {
        console.log("Returning empty projects array due to:", {
          isError,
          hasResults: !!autodeskProjectsData?.results
        });
      }
      return [];
    }
    
    const mappedProjects = autodeskProjectsData.results
      .filter((project: any) => project.id) // Filter out projects with null/undefined id
      .map((project: any) => {
        // Debug logging - only in verbose development mode
        if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_VERBOSE === 'true') {
          console.log("Mapping project:", project);
        }
        
        // Use ACC project ID for API calls, but keep database ID for URL routing
        const accProjectId = project.acc_project_id || project.id;
        
        return {
          id: accProjectId, // Use ACC project ID for API calls
          databaseId: project.id, // Keep database ID for reference
          hubId: project.acc_hub_id, // Use acc_hub_id from API response
          hubName: project.hub_name || project.hubName, // Use hub_name from API response
          account: project.hub_name || project.hubName, // Map hub_name to account for display
          name: project.name,
          status: project.status || "Active", // New API returns status directly
          createdAt: project.createdAt,
          updatedAt: project.updatedAt,
        };
      });
    
    // Debug logging - only in verbose development mode
    if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_VERBOSE === 'true') {
      console.log("Final mapped projects:", mappedProjects);
    }
    return mappedProjects;
  }, [isError, connectionStatus, autodeskProjectsData, isVercelDeployment]);

  // Update selected project when the projects list changes (stabilized)
  useEffect(() => {
    console.log("Project selection effect triggered:", {
      projectsLength: projects.length,
      selectedProjectId: selectedProject?.id,
      selectedProjectName: selectedProject?.name,
      availableProjects: projects.map(p => ({ id: p.id, name: p.name || 'Unnamed Project' }))
    });
    
    // Extract project ID from current URL
    const urlProjectId = location.split('/projects/')[1]?.split('/')[0];
    
    if (projects.length > 0) {
      // If we have a project ID in URL, try to find and select that project
      if (urlProjectId) {
        console.log("Looking for project in projects list:", {
          urlProjectId,
          availableProjectIds: projects.map(p => p.id),
          availableProjectNames: projects.map(p => p.name || 'Unnamed Project')
        });
        
        // Try to find project by database ID first (for URL routing)
        let urlProject = projects.find(p => p.databaseId === urlProjectId);
        
        // If not found, try to find by ACC project ID
        if (!urlProject) {
          urlProject = projects.find(p => p.id === urlProjectId);
        }
        
        // If still not found, try to find by adding b. prefix or removing it
        if (!urlProject) {
          if (urlProjectId.startsWith('b.')) {
            // Try without b. prefix
            const projectIdWithoutPrefix = urlProjectId.substring(2);
            urlProject = projects.find(p => p.databaseId === projectIdWithoutPrefix);
          } else {
            // Try with b. prefix
            const projectIdWithPrefix = `b.${urlProjectId}`;
            urlProject = projects.find(p => p.id === projectIdWithPrefix);
          }
        }
        
        if (urlProject) {
          console.log("Found project from URL:", urlProject.name || 'Unnamed Project', "ID:", urlProject.id);
          setSelectedProject(urlProject);
          return;
        } else {
          console.log("Project from URL not found in projects list:", urlProjectId);
          console.log("Available projects:", projects);
        }
      }
      
      // Only auto-select if no project is currently selected AND projects are available
      // AND we're not on a project-specific URL (to avoid overriding user selection)
      if (!selectedProject && !location.includes('/projects/')) {
        // Check if the default project exists in the projects list
        const defaultProject = projects.find(p => p.id === 'b.c53cca91-64e3-4f2d-b329-75d89031bc99');
        const projectToSelect = defaultProject || projects[0];
        
        console.log("Auto-selecting project:", projectToSelect.name || 'Unnamed Project', "ID:", projectToSelect.id);
        setSelectedProject(projectToSelect);
        
        // Navigate to project-specific URL if not already there
        const currentPath = location.split('/')[1] || 'dashboard';
        const expectedUrl = `/${currentPath}/projects/${projectToSelect.id}`;
        if (location !== expectedUrl && !location.includes('/projects/')) {
          console.log("Navigating to:", expectedUrl);
          navigate(expectedUrl);
        }
        
        console.log(
          "Automatically selected project:",
          projectToSelect.name || 'Unnamed Project',
          defaultProject ? "(default)" : "(first available)"
        );
      }
    } else if (urlProjectId && !selectedProject) {
      // Handle URL project ID when projects are still loading
      // Create a temporary project object while projects are loading
      const tempProject: Project = {
        id: urlProjectId,
        name: 'Loading...',
        hubId: '',
        status: 'active'
      };
      
      console.log("Setting temporary project from URL while loading:", urlProjectId);
      setSelectedProject(tempProject);
    }
    // Never clear selection during loading or re-renders
    // This prevents flickering and data disappearing during API refetches
  }, [projects.length, location, navigate, setSelectedProject]);

  // Debug: Log current connection status and account ID (enhanced debugging)
  if (import.meta.env.DEV && CONFIG.DEBUG_LEVEL === 'verbose') {
    console.log("Header - Connection Status:", connectionStatus);
    console.log("Header - Autodesk Projects Data:", autodeskProjectsData);
    console.log("Header - Is Loading Projects:", isLoadingProjects);
    console.log("Header - Is Error:", isError, error?.message);
    console.log("Header - Global Selected Project:", selectedProject?.name || null);
    console.log("Header - Projects Count:", projects.length);
    console.log("Header - Projects List:", projects);
  }

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    // Use ACC-style URL pattern for project navigation based on current route
    const currentPath = location.split('/')[1] || 'dashboard'; // Extract current feature from URL
    navigate(`/${currentPath}/projects/${project.id}`);
  };

  const filteredProjects = projects.filter((project) =>
    (project.name || '').toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-white shadow-sm h-16 px-6 flex items-center justify-between transition-all duration-300 ${sidebarOpen ? 'md:left-64' : 'md:left-16'}`}>
      <div className="flex items-center">
        <Button
          variant="ghost"
          className="mr-3 md:hidden"
          onClick={toggleSidebar}
          size="icon"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Project Switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center space-x-2 px-2 py-1 -ml-2"
            >
              <Building className="h-4 w-4 text-gray-600 mr-1" />
              <span className="font-semibold text-lg">
                {selectedProject?.name || "Select a Project"}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-600" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[420px] max-w-[90vw] bg-gray-50 border-gray-200 z-[10000]">
            <DropdownMenuLabel className="text-gray-700">
              Switch Project
              {isLoadingProjects && connectionStatus?.configured && (
                <span className="text-xs text-gray-500 ml-2">(Loading...)</span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-gray-300" />

            <div className="p-2">
              <Input
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white"
                onClick={(e) => e.preventDefault()}
              />
            </div>

            {isError && (
              <div className="px-3 py-2 text-xs text-red-600 bg-red-50 mx-2 my-1 rounded border border-red-200">
                <p className="font-semibold">Error loading projects</p>
                <p>{error?.message || "An unknown error occurred."}</p>
              </div>
            )}

            {!isError &&
              filteredProjects.length === 0 &&
              !isLoadingProjects && (
                <div className="px-3 py-4 text-center text-xs text-gray-500">
                  No projects found.
                </div>
              )}
            <ScrollArea className="h-[400px]">
                             {filteredProjects.map((project: Project, index: number) => {
                 const isCurrentProject = selectedProject?.id === project.id;
                 return (
                <DropdownMenuItem
                  key={project.id || `project-${index}`}
                  className={`py-2.5 px-2.5 cursor-pointer focus:bg-white hover:bg-white ${
                    isCurrentProject 
                      ? 'bg-blue-50 border-l-4 border-blue-500' 
                      : 'bg-transparent'
                  }`}
                  onClick={() => handleProjectSelect(project)}
                >
                  <div className="flex items-start w-full min-w-0">
                    <Building className={`h-4 w-4 mr-1.5 flex-shrink-0 mt-0.5 ${
                      isCurrentProject ? 'text-blue-600' : 'text-gray-600'
                    }`} />
                    <div className="flex-1 min-w-0 pr-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className={`truncate text-sm font-medium block ${
                          isCurrentProject ? 'text-blue-900 font-semibold' : 'text-gray-900'
                        }`}>
                          {project.name || 'Unnamed Project'}
                        </span>
                        {isCurrentProject && (
                          <span className="text-xs text-blue-600 whitespace-nowrap flex-shrink-0">(Current)</span>
                        )}
                      </div>
                      <div className={`text-xs mt-1 truncate ${
                        isCurrentProject ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                        {project.hubName || project.account || 'Unknown Hub'}
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
                );
              })}
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* ACC Connection Indicator */}
        {connectionStatus && !connectionStatus.configured && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="ml-3 text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-100 hover:text-amber-700"
              >
                Not Connected to ACC
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-2">
                <h4 className="font-medium">Autodesk Construction Cloud</h4>
                <p className="text-sm text-gray-500">
                  Connect to ACC to access project files and folders. This will
                  enable the document browser to display your actual project
                  structure.
                </p>
                <Link href="/settings">
                  <Button size="sm" className="mt-2">
                    Configure Connection
                  </Button>
                </Link>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      <div className="flex items-center space-x-4">
        {/* MyHome and Tenderer buttons */}
        <div className="flex items-center space-x-2">
          <Link href="/myhome">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            >
              <Home className="h-4 w-4" />
              <span>MyHome</span>
            </Button>
          </Link>
          
          <Button
            variant="outline"
            size="sm"
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            onClick={() => window.open('/tenderer', '_blank')}
          >
            <ExternalLink className="h-4 w-4" />
            <span>Tenderer</span>
          </Button>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center space-x-3 hover:bg-gray-50 rounded-xl p-3 transition-all duration-200 border border-transparent hover:border-gray-200 hover:shadow-sm">
              <AuthenticatedAvatar />
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72 mt-2 border border-gray-200 shadow-xl bg-white rounded-xl p-2">
            <UserProfileSection />
            <DropdownMenuSeparator className="my-3 bg-gray-100" />
            <DropdownMenuItem 
              onClick={() => logout()}
              className="flex items-center space-x-2 p-3 rounded-lg hover:bg-red-50 text-red-600 cursor-pointer transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="font-medium">Logout</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

function AuthenticatedAvatar() {
  const { user } = useAuth();
  
  // Use email first letter, fallback to firstName, then 'U'
  const firstLetter = (user as any)?.email?.charAt(0)?.toUpperCase() || 
                     (user as any)?.firstName?.charAt(0)?.toUpperCase() || 'U';
  
  return (
    <Avatar>
      <AvatarFallback className="bg-blue-500 text-white font-semibold">{firstLetter}</AvatarFallback>
    </Avatar>
  );
}

function UserProfileSection() {
  const { user, permissions } = useAuth();
  
  if (!user) return null;
  
  const permissionLabels = {
    app_admin: "App Administrator",
    account_admin: "Account Administrator", 
    project_admin: "Project Administrator",
    tender_user: "Tenderer"
  };

  const permissionColors = {
    app_admin: "bg-purple-100 text-purple-700 border-purple-200",
    account_admin: "bg-blue-100 text-blue-700 border-blue-200",
    project_admin: "bg-green-100 text-green-700 border-green-200",
    tender_user: "bg-orange-100 text-orange-700 border-orange-200"
  };
  
  // Use user.permissionLevel as fallback if permissions object is not available
  const permissionLevel = (permissions as any)?.level || (user as any)?.permissionLevel;
  
  return (
    <div className="p-4">
      <div className="flex items-center space-x-3">
        <AuthenticatedAvatar />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {(user as any).firstName} {(user as any).lastName}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {(user as any).email}
          </p>
          {permissionLevel && (
            <div className="mt-2">
              <Badge 
                className={`text-xs font-medium px-2 py-1 rounded-md border ${
                  permissionColors[permissionLevel as keyof typeof permissionColors] || "bg-gray-100 text-gray-700 border-gray-200"
                }`}
              >
                {permissionLabels[permissionLevel as keyof typeof permissionLabels] || permissionLevel}
              </Badge>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Header;
